import * as React from "react";
import {
  Box,
  IconButton,
  Button,
  CircularProgress,
  TextField,
  Paper,
} from "@mui/material";
import CloseIcon from "@mui/icons-material/Close";
import AutoAwesomeIcon from "@mui/icons-material/AutoAwesome";
import PlayArrowIcon from "@mui/icons-material/PlayArrow";

import {
  //normalizeGameWideAiResult,
  GameWideAiTurn,
  GameWideAiResult,
  GameWideNotableRally,
  GameWideNotableShot,
} from "../../shared/types/askAIGameQuestionsTypes";
import { RenderPlayerIdentity } from "./GameStoryDialog_UPDATED";
// import { normalizeGameWideAiResult } from "../../shared/types/askAiBasicQuestionTypes";

export function normalizeGameWideAiResult(v: any): GameWideAiResult | null {
  if (!v || typeof v !== "object") return null;

  if (typeof v.raw === "string" && v.raw.trim()) {
    try {
      return normalizeGameWideAiResult(JSON.parse(v.raw));
    } catch {
      return null;
    }
  }

  const answer =
    typeof v.answer === "string" && v.answer.trim()
      ? v.answer.trim()
      : typeof v.highLevelAnswer === "string" && v.highLevelAnswer.trim()
        ? v.highLevelAnswer.trim()
        : typeof v.turningPointSummary === "string" &&
            v.turningPointSummary.trim()
          ? v.turningPointSummary.trim()
          : v.summary?.keyTurningPointDescription
            ? v.summary.keyTurningPointDescription.trim()
            : Array.isArray(v.turningPointsSummary)
              ? "Key turning points identified across several rallies."
              : null;

  if (!answer) return null;

  const rawNotableRallies = Array.isArray(v.notableRallies)
    ? v.notableRallies
    : Array.isArray(v.turningPointsSummary)
      ? v.turningPointsSummary
      : [];

  const notableRallies: GameWideNotableRally[] = rawNotableRallies
    .map((r: any) => {
      if (!r || typeof r !== "object" || !Number.isFinite(r.rallyIndex))
        return null;

      const whyImportant =
        r.whyImportant ??
        r.whyItMatters ??
        r.whyTurningPoint ??
        r.whyItMattered ??
        r.description ??
        r.reason ??
        "";

      const supportingShots: GameWideNotableShot[] = Array.isArray(
        r.supportingShots,
      )
        ? r.supportingShots
            .map((s: any) => {
              if (
                s &&
                typeof s === "object" &&
                Number.isFinite(s.shotIndex) &&
                typeof s.reason === "string" &&
                s.reason.trim()
              ) {
                return {
                  shotIndex: Number(s.shotIndex),
                  shotNumber: Number.isFinite(s.shotNumber)
                    ? Number(s.shotNumber)
                    : Number(s.shotIndex) + 1,
                  reason: s.reason.trim(),
                };
              }
              return null;
            })
            .filter((s: any): s is GameWideNotableShot => !!s)
        : [];

      return {
        rallyIndex: Number(r.rallyIndex),
        rallyNumber: Number.isFinite(r.rallyNumber)
          ? Number(r.rallyNumber)
          : Number(r.rallyIndex) + 1,
        title:
          typeof r.title === "string" && r.title.trim()
            ? r.title.trim()
            : `Rally ${Number(r.rallyIndex) + 1}`,
        whyImportant,
        uiComment:
          typeof r.uiComment === "string" && r.uiComment.trim()
            ? r.uiComment.trim()
            : whyImportant,
        importance:
          r.importance === "critical" ||
          r.importance === "high" ||
          r.importance === "medium"
            ? r.importance
            : "high",
        scoreAtStart:
          Array.isArray(r.scoreAtStart) &&
          r.scoreAtStart.length === 2 &&
          Number.isFinite(r.scoreAtStart[0]) &&
          Number.isFinite(r.scoreAtStart[1])
            ? ([Number(r.scoreAtStart[0]), Number(r.scoreAtStart[1])] as [
                number,
                number,
              ])
            : r.scoreContext &&
                typeof r.scoreContext === "object" &&
                Array.isArray(r.scoreContext.scoreBefore) &&
                r.scoreContext.scoreBefore.length === 2 &&
                Number.isFinite(r.scoreContext.scoreBefore[0]) &&
                Number.isFinite(r.scoreContext.scoreBefore[1])
              ? ([
                  Number(r.scoreContext.scoreBefore[0]),
                  Number(r.scoreContext.scoreBefore[1]),
                ] as [number, number])
              : null,
        winningTeam:
          r.winningTeam === 0 || r.winningTeam === 1 ? r.winningTeam : null,
        supportingShots,
      };
    })
    .filter((r: any): r is GameWideNotableRally => !!r);
  return {
    answer,
    shortSummary:
      typeof v.shortSummary === "string" && v.shortSummary.trim()
        ? v.shortSummary.trim()
        : v.summary &&
            typeof v.summary === "object" &&
            typeof v.summary.gameFlow === "string" &&
            v.summary.gameFlow.trim()
          ? v.summary.gameFlow.trim()
          : undefined,
    notableRallies,
    notablePatterns: Array.isArray(v.notablePatterns)
      ? v.notablePatterns
      : undefined,
    suggestedFollowups: Array.isArray(v.suggestedFollowups)
      ? v.suggestedFollowups
      : undefined,
  };
}

export function GameStoryGameAIPopup(props: {
  open: boolean;
  onClose: () => void;
  packageId: string;
  onOpenRally: (rallyIndex: number) => void;
  renderPlayerIdentity: RenderPlayerIdentity;
}) {
  const { open, onClose, packageId, onOpenRally, renderPlayerIdentity } = props;

  const storageKey = `gameAiTurns:${packageId}`;

  React.useEffect(() => {
    if (!packageId) return;

    setStorageHydrated(false);

    try {
      const raw = window.localStorage.getItem(storageKey);

      if (!raw) {
        setTurns([]);
        setSelectedTurnId(null);
        setStorageHydrated(true);
        return;
      }

      const parsed = JSON.parse(raw);
      const savedTurns = Array.isArray(parsed?.turns) ? parsed.turns : [];
      const savedSelectedTurnId =
        typeof parsed?.selectedTurnId === "string"
          ? parsed.selectedTurnId
          : null;

      setTurns(savedTurns);
      setSelectedTurnId(savedSelectedTurnId ?? savedTurns[0]?.id ?? null);
    } catch {
      setTurns([]);
      setSelectedTurnId(null);
    } finally {
      setStorageHydrated(true);
    }
  }, [packageId, storageKey]);

  const [question, setQuestion] = React.useState(
    "What were the biggest turning points in this game?",
  );
  const [loading, setLoading] = React.useState(false);
  const [error, setError] = React.useState<string | null>(null);
  const [turns, setTurns] = React.useState<GameWideAiTurn[]>([]);
  const [selectedTurnId, setSelectedTurnId] = React.useState<string | null>(
    null,
  );

  //   const hasHydratedStorageRef = React.useRef(false);
  const [storageHydrated, setStorageHydrated] = React.useState(false);

  const [panelPos, setPanelPos] = React.useState({ x: 20, y: 50 });
  const dragStateRef = React.useRef<{
    dragging: boolean;
    startMouseX: number;
    startMouseY: number;
    startPanelX: number;
    startPanelY: number;
  }>({
    dragging: false,
    startMouseX: 0,
    startMouseY: 0,
    startPanelX: 20,
    startPanelY: 50,
  });

  const presets = React.useMemo(
    () => [
      "What were the biggest turning points in this game?",
      "What momentum swings mattered most?",
      "What recurring tactical patterns show up in this game?",
      "Which rallies were most important and why?",
      // "Where did Team 0 gain the most control?",
      // "Where did Team 1 gain the most control?",
    ],
    [],
  );

  const runQuestion = React.useCallback(
    async (q: string) => {
      const trimmed = q.trim();
      if (!trimmed) return;

      setLoading(true);
      setError(null);

      try {
        const res = await fetch("/api/ai-ask-game-question", {
          method: "POST",
          headers: { "Content-Type": "application/json" },
          body: JSON.stringify({
            packageId,
            question: trimmed,
            maxReturnedRallies: 4,
          }),
        });

        const json = await res.json();

        if (!res.ok) {
          throw new Error(
            typeof json?.details === "string"
              ? json.details
              : typeof json?.error === "string"
                ? json.error
                : "Failed to analyze game",
          );
        }

        const newTurn = json?.turn ?? null;

        if (newTurn) {
          setTurns((prev) => [
            newTurn,
            ...prev.filter((t) => t.id !== newTurn.id),
          ]);
          setSelectedTurnId(newTurn.id);
        }
      } catch (err: any) {
        setError(
          typeof err?.message === "string"
            ? err.message
            : "Failed to analyze game",
        );
      } finally {
        setLoading(false);
      }
    },
    [packageId],
  );

  const selectedTurn = React.useMemo(() => {
    if (!turns.length) return null;
    if (!selectedTurnId) return turns[0] ?? null;
    return turns.find((t) => t.id === selectedTurnId) ?? turns[0] ?? null;
  }, [turns, selectedTurnId]);

  //   const result: GameWideAiResult | null = React.useMemo(() => {
  //     const resultHere = selectedTurn?.result;
  //     if (!resultHere || typeof resultHere !== "object") return null;

  //     if (
  //       typeof (resultHere as any).answer === "string" &&
  //       Array.isArray((resultHere as any).notableRallies)
  //     ) {
  //       return resultHere as GameWideAiResult;
  //     }

  //     return null;
  //   }, [selectedTurn]);

  const result: GameWideAiResult | null = React.useMemo(() => {
    return normalizeGameWideAiResult(selectedTurn?.result);
  }, [selectedTurn]);

  const deleteTurn = React.useCallback(
    (turnId: string) => {
      setTurns((prev) => {
        const next = prev.filter((t) => t.id !== turnId);
        if (selectedTurnId === turnId) {
          setSelectedTurnId(next[0]?.id ?? null);
        }
        return next;
      });
    },
    [selectedTurnId],
  );

  const clearTurns = React.useCallback(() => {
    setTurns([]);
    setSelectedTurnId(null);
  }, []);

  React.useEffect(() => {
    if (!packageId) return;
    if (!storageHydrated) return;

    try {
      window.localStorage.setItem(
        storageKey,
        JSON.stringify({
          turns,
          selectedTurnId,
        }),
      );
    } catch {
      // ignore localStorage failures
    }
  }, [packageId, storageKey, turns, selectedTurnId, storageHydrated]);

  React.useEffect(() => {
    if (!open) return;
    setError(null);
  }, [open]);

  React.useEffect(() => {
    if (!open) return;

    function onPointerMove(e: PointerEvent) {
      const s = dragStateRef.current;
      if (!s.dragging) return;

      const nextX = s.startPanelX + (e.clientX - s.startMouseX);
      const nextY = s.startPanelY + (e.clientY - s.startMouseY);

      const width = 980;
      const height = Math.min(840, window.innerHeight - 48);
      const minX = 8;
      const minY = 8;
      const maxX = Math.max(8, window.innerWidth - width - 8);
      const maxY = Math.max(8, window.innerHeight - height - 8);

      setPanelPos({
        x: Math.min(Math.max(minX, nextX), maxX),
        y: Math.min(Math.max(minY, nextY), maxY),
      });
    }

    function onPointerUp() {
      dragStateRef.current.dragging = false;
    }

    window.addEventListener("pointermove", onPointerMove);
    window.addEventListener("pointerup", onPointerUp);

    return () => {
      window.removeEventListener("pointermove", onPointerMove);
      window.removeEventListener("pointerup", onPointerUp);
    };
  }, [open]);

  const startDrag = React.useCallback(
    (e: React.PointerEvent<HTMLDivElement>) => {
      if ((e.target as HTMLElement).closest("button")) return;

      dragStateRef.current = {
        dragging: true,
        startMouseX: e.clientX,
        startMouseY: e.clientY,
        startPanelX: panelPos.x,
        startPanelY: panelPos.y,
      };
    },
    [panelPos.x, panelPos.y],
  );

  if (!open) return null;

  return (
    <Paper
      elevation={10}
      sx={{
        position: "fixed",
        left: panelPos.x,
        top: panelPos.y,
        width: 1160, // 980,
        maxWidth: "calc(100vw - 20px)",
        height: "min(860px, calc(100vh - 20px))",
        bgcolor: "rgba(24,24,24,0.98)",
        color: "rgba(255,255,255,0.92)",
        backgroundImage: "none",
        border: "1px solid rgba(255,255,255,0.10)",
        borderRadius: 2,
        zIndex: 1800,
        overflow: "hidden",
        display: "flex",
        flexDirection: "column",
      }}
    >
      <Box
        onPointerDown={startDrag}
        sx={{
          pr: 6,
          borderBottom: "1px solid rgba(255,255,255,0.10)",
          display: "flex",
          alignItems: "center",
          gap: 1,
          cursor: "move",
          userSelect: "none",
          minHeight: 52,
          px: 2,
          flex: "0 0 auto",
        }}
      >
        <AutoAwesomeIcon sx={{ fontSize: 20 }} />
        <Box sx={{ fontWeight: 800, fontSize: 18 }}>Full Game AI (WIP)</Box>
        <IconButton
          onClick={onClose}
          sx={{
            position: "absolute",
            right: 8,
            top: 8,
            color: "rgba(255,255,255,0.75)",
          }}
        >
          <CloseIcon />
        </IconButton>
      </Box>

      <Box sx={{ p: 0, display: "flex", minHeight: 0, flex: 1 }}>
        <Box
          sx={{
            flex: 1,
            minWidth: 0,
            display: "flex",
            flexDirection: "column",
            p: 2,
            gap: 1.5,
            overflow: "hidden",
          }}
        >
          {!selectedTurn && !loading ? (
            <Box
              sx={{
                p: 2,
                borderRadius: 1.5,
                bgcolor: "rgba(255,255,255,0.03)",
                border: "1px solid rgba(255,255,255,0.10)",
                fontSize: 13,
                opacity: 0.85,
              }}
            >
              Ask a whole-game question and this panel will return an answer
              plus notable rallies you can jump to.
            </Box>
          ) : null}

          {selectedTurn && !result && !loading ? (
            <Box
              sx={{
                p: 2,
                borderRadius: 1.5,
                bgcolor: "rgba(255,80,80,0.08)",
                border: "1px solid rgba(255,80,80,0.20)",
                fontSize: 13,
                opacity: 0.92,
              }}
            >
              This turn did not come back in the expected shared
              GameWideAiResult shape.
            </Box>
          ) : null}

          {result && selectedTurn ? (
            <>
              <Box
                sx={{
                  p: 1.5,
                  borderRadius: 1.5,
                  bgcolor: "rgba(102,179,255,0.07)",
                  border: "1px solid rgba(102,179,255,0.20)",
                }}
              >
                <Box
                  sx={{ fontSize: 11, fontWeight: 800, opacity: 0.65, mb: 0.5 }}
                >
                  Question
                </Box>
                <Box sx={{ fontSize: 17, fontWeight: 900, lineHeight: 1.35 }}>
                  {selectedTurn.question}
                </Box>
                <Box sx={{ mt: 0.75, fontSize: 12, opacity: 0.55 }}>
                  {new Date(selectedTurn.createdAt).toLocaleString()}
                </Box>
              </Box>

              <Box
                sx={{
                  p: 1.25,
                  borderRadius: 1.5,
                  bgcolor: "rgba(255,255,255,0.03)",
                  border: "1px solid rgba(255,255,255,0.10)",
                }}
              >
                <Box
                  sx={{ fontSize: 12, fontWeight: 800, opacity: 0.7, mb: 0.6 }}
                >
                  Answer
                </Box>
                <Box
                  sx={{
                    p: 1.5,
                    borderRadius: 1.5,
                    bgcolor: "rgba(255,255,255,0.03)",
                    border: "1px solid rgba(255,255,255,0.10)",
                    maxHeight: 220,
                    fontSize: 14,
                    lineHeight: 1.5,
                    pr: 0.5,
                    overflowY: "auto",
                    flex: "0 0 auto",
                  }}
                >
                  {result.answer}
                </Box>

                {result.shortSummary ? (
                  <Box
                    sx={{
                      mt: 0.9,
                      fontSize: 13,
                      lineHeight: 1.45,
                      opacity: 0.8,
                    }}
                  >
                    {result.shortSummary}
                  </Box>
                ) : null}
              </Box>

              <Box sx={{ fontSize: 12, fontWeight: 800, opacity: 0.75 }}>
                Notable rallies
              </Box>

              <Box
                sx={{
                  flex: 1,
                  minHeight: 0,
                  overflowY: "auto",
                  display: "flex",
                  flexDirection: "column",
                  gap: 1,
                  pr: 0.5,
                }}
              >
                {result.notableRallies.map((r) => (
                  <Box
                    key={`${r.rallyIndex}-${r.title}`}
                    sx={{
                      p: 1.1,
                      borderRadius: 1.5,
                      bgcolor: "rgba(255,255,255,0.03)",
                      border: "1px solid rgba(255,255,255,0.10)",
                    }}
                  >
                    <Box
                      sx={{
                        display: "flex",
                        alignItems: "center",
                        justifyContent: "space-between",
                        gap: 1,
                        mb: 0.75,
                      }}
                    >
                      <Box
                        sx={{
                          fontWeight: 800,
                          fontSize: 15.5,
                          lineHeight: 1.25,
                        }}
                      >
                        {r.title}
                      </Box>

                      <Box
                        sx={{
                          px: 1,
                          py: 0.35,
                          borderRadius: 999,
                          fontSize: 11,
                          fontWeight: 900,
                          textTransform: "uppercase",
                          bgcolor:
                            r.importance === "critical"
                              ? "rgba(255,110,110,0.14)"
                              : r.importance === "high"
                                ? "rgba(255,190,90,0.14)"
                                : "rgba(255,255,255,0.08)",
                          border:
                            r.importance === "critical"
                              ? "1px solid rgba(255,110,110,0.32)"
                              : r.importance === "high"
                                ? "1px solid rgba(255,190,90,0.28)"
                                : "1px solid rgba(255,255,255,0.14)",
                        }}
                      >
                        {r.importance}
                      </Box>
                    </Box>

                    <Box
                      sx={{
                        display: "flex",
                        alignItems: "center",
                        gap: 1,
                        flexWrap: "wrap",
                        mb: 0.6,
                      }}
                    >
                      <Box
                        sx={{ fontWeight: 800, fontSize: 14, lineHeight: 1 }}
                      >
                        Rally number {r.rallyNumber}
                      </Box>

                      <Button
                        size="small"
                        variant="outlined"
                        startIcon={<PlayArrowIcon />}
                        onClick={() => onOpenRally(r.rallyIndex)}
                        sx={{
                          textTransform: "none",
                          color: "rgba(255,255,255,0.88)",
                          borderColor: "rgba(255,255,255,0.16)",
                          minHeight: 28,
                          px: 1,
                          "&:hover": {
                            borderColor: "rgba(255,255,255,0.28)",
                            backgroundColor: "rgba(255,255,255,0.05)",
                          },
                        }}
                      >
                        Open rally
                      </Button>
                    </Box>

                    <Box
                      sx={{
                        fontSize: 14,
                        lineHeight: 1.5,
                        mb: 0.65,
                        opacity: 0.95,
                      }}
                    >
                      {r.whyImportant}
                    </Box>

                    {r.uiComment && r.uiComment !== r.whyImportant ? (
                      <Box
                        sx={{
                          fontSize: 13,
                          lineHeight: 1.45,
                          opacity: 0.78,
                          mb: 0.65,
                        }}
                      >
                        {r.uiComment}
                      </Box>
                    ) : null}

                    {r.supportingShots?.length ? (
                      <Box sx={{ mb: 1 }}>
                        <Box
                          sx={{
                            fontSize: 12,
                            fontWeight: 800,
                            opacity: 0.72,
                            mb: 0.45,
                          }}
                        >
                          Supporting shots
                        </Box>
                        <Box
                          sx={{
                            display: "flex",
                            flexDirection: "column",
                            gap: 0.4,
                          }}
                        >
                          {r.supportingShots.map((s) => (
                            <Box
                              key={`${r.rallyIndex}-${s.shotIndex}`}
                              sx={{
                                fontSize: 13,
                                opacity: 0.88,
                                lineHeight: 1.4,
                              }}
                            >
                              {/* Shot {s.shotIndex + 1}: {s.reason} */}
                              Shot {s.shotNumber ?? s.shotIndex + 1}: {s.reason}
                            </Box>
                          ))}
                        </Box>
                      </Box>
                    ) : null}

                    {/* <Box sx={{ display: "flex", gap: 1 }}>
                      <Button
                        size="small"
                        variant="outlined"
                        startIcon={<PlayArrowIcon />}
                        onClick={() => onOpenRally(r.rallyIndex)}
                        sx={{
                          textTransform: "none",
                          color: "rgba(255,255,255,0.88)",
                          borderColor: "rgba(255,255,255,0.16)",
                          "&:hover": {
                            borderColor: "rgba(255,255,255,0.28)",
                            backgroundColor: "rgba(255,255,255,0.05)",
                          },
                        }}
                      >
                        Open rally
                      </Button>
                    </Box> */}
                  </Box>
                ))}

                {!result.notableRallies.length ? (
                  <Box sx={{ fontSize: 12, opacity: 0.7 }}>
                    No notable rallies were returned.
                  </Box>
                ) : null}
              </Box>
            </>
          ) : loading ? (
            <Box sx={{ p: 2, fontSize: 13, opacity: 0.8 }}>
              Analyzing game...
            </Box>
          ) : null}
        </Box>

        <Box
          sx={{
            width: 320,
            borderRight: "1px solid rgba(255,255,255,0.10)",
            p: 1.5,
            display: "flex",
            flexDirection: "column",
            gap: 1,
            minHeight: 0,
            flex: "0 0 auto",
          }}
        >
          <Box sx={{ fontSize: 13, fontWeight: 800, opacity: 0.9 }}>
            Ask about the full game (from PB Vision insights.json file)
          </Box>

          <TextField
            multiline
            minRows={5}
            maxRows={10}
            value={question}
            onChange={(e) => setQuestion(e.target.value)}
            placeholder="Ask about turning points, momentum swings, tactical patterns, important rallies..."
            fullWidth
            variant="outlined"
            sx={{
              "& .MuiInputBase-root": {
                bgcolor: "rgba(255,255,255,0.05)",
                color: "rgba(255,255,255,0.92)",
                alignItems: "flex-start",
              },
              "& .MuiOutlinedInput-notchedOutline": {
                borderColor: "rgba(255,255,255,0.16)",
              },
              "&:hover .MuiOutlinedInput-notchedOutline": {
                borderColor: "rgba(255,255,255,0.28)",
              },
              "& .MuiInputBase-input::placeholder": {
                color: "rgba(255,255,255,0.45)",
                opacity: 1,
              },
            }}
          />

          <Button
            variant="contained"
            onClick={() => runQuestion(question)}
            disabled={loading || !question.trim()}
            startIcon={
              loading ? (
                <CircularProgress size={16} color="inherit" />
              ) : (
                <AutoAwesomeIcon />
              )
            }
            sx={{
              textTransform: "none",
              fontWeight: 800,
              bgcolor: "rgba(102,179,255,0.24)",
              border: "1px solid rgba(102,179,255,0.35)",
              "&:hover": {
                bgcolor: "rgba(102,179,255,0.34)",
              },
            }}
          >
            {loading ? "Analyzing..." : "Ask Game AI"}
          </Button>

          {error ? (
            <Box
              sx={{
                mt: 1,
                p: 1,
                borderRadius: 1,
                bgcolor: "rgba(255,80,80,0.10)",
                border: "1px solid rgba(255,80,80,0.24)",
                color: "rgba(255,170,170,0.95)",
                fontSize: 16,
              }}
            >
              {error}
            </Box>
          ) : null}

          <Box sx={{ mt: 1, fontSize: 12, fontWeight: 800, opacity: 0.8 }}>
            Quick prompts
          </Box>

          <Box sx={{ display: "flex", flexDirection: "column", gap: 0.75 }}>
            {presets.map((p) => (
              <Button
                key={p}
                variant="outlined"
                size="small"
                onClick={() => {
                  setQuestion(p);
                  runQuestion(p);
                }}
                sx={{
                  justifyContent: "flex-start",
                  textTransform: "none",
                  fontSize: 12,
                  color: "rgba(255,255,255,0.86)",
                  borderColor: "rgba(255,255,255,0.14)",
                  backgroundColor: "rgba(255,255,255,0.03)",
                  "&:hover": {
                    borderColor: "rgba(255,255,255,0.24)",
                    backgroundColor: "rgba(255,255,255,0.06)",
                  },
                }}
              >
                {p}
              </Button>
            ))}
          </Box>

          <Box
            sx={{
              mt: 1,
              pt: 1.25,
              borderTop: "1px solid rgba(255,255,255,0.10)",
              display: "flex",
              alignItems: "center",
              justifyContent: "space-between",
            }}
          >
            <Box sx={{ fontSize: 12, fontWeight: 800, opacity: 0.8 }}>
              Previous turns
            </Box>

            <Button
              size="small"
              variant="text"
              onClick={clearTurns}
              disabled={!turns.length}
              sx={{
                minWidth: 0,
                px: 0.5,
                textTransform: "none",
                color: "rgba(255,255,255,0.65)",
              }}
            >
              Clear
            </Button>
          </Box>

          <Box
            sx={{
              minHeight: 0,
              flex: 1,
              overflowY: "auto",
              display: "flex",
              flexDirection: "column",
              gap: 0.75,
              pr: 0.5,
            }}
          >
            {turns.map((t) => {
              const isSelected = t.id === selectedTurnId;
              return (
                <Box
                  key={t.id}
                  sx={{
                    p: 1,
                    borderRadius: 1.25,
                    border: isSelected
                      ? "1px solid rgba(102,179,255,0.45)"
                      : "1px solid rgba(255,255,255,0.12)",
                    bgcolor: isSelected
                      ? "rgba(102,179,255,0.10)"
                      : "rgba(255,255,255,0.03)",
                  }}
                >
                  <Box
                    onClick={() => setSelectedTurnId(t.id)}
                    sx={{ cursor: "pointer" }}
                  >
                    <Box
                      sx={{
                        fontSize: 12,
                        fontWeight: 800,
                        lineHeight: 1.35,
                        mb: 0.5,
                      }}
                    >
                      {t.question}
                    </Box>
                    <Box sx={{ fontSize: 11, opacity: 0.6 }}>
                      {new Date(t.createdAt).toLocaleString()}
                    </Box>
                  </Box>

                  <Box
                    sx={{
                      display: "flex",
                      justifyContent: "flex-end",
                      mt: 0.75,
                    }}
                  >
                    <Button
                      size="small"
                      variant="text"
                      onClick={() => deleteTurn(t.id)}
                      sx={{
                        minWidth: 0,
                        px: 0.5,
                        textTransform: "none",
                        fontSize: 11,
                        color: "rgba(255,140,140,0.88)",
                      }}
                    >
                      Delete
                    </Button>
                  </Box>
                </Box>
              );
            })}

            {!turns.length ? (
              <Box sx={{ fontSize: 12, opacity: 0.6 }}>No turns yet.</Box>
            ) : null}
          </Box>
        </Box>
      </Box>
    </Paper>
  );
}
